/**
 * c-mini-cart-v2.js
 * ---------------------------------------------------------------------------
 * Self-contained controller for the redesigned cart drawer (section: c-mini-cart-v2).
 *
 * This is a DROP-IN replacement for cart.js + cart-drawer.js and is loaded ONLY when
 * `settings.cart_v2_enabled` is true (see sections/header.liquid). It deliberately keeps
 * the same custom-element tag names the rest of the theme relies on
 * (<mini-cart>, <cart-items>, <cart-remove-button>) so that product-form.js, global.js,
 * color-swatches.js, gift-wrapping.js, etc. keep working unchanged — they just end up
 * rendering the new `c-mini-cart-v2` section instead of the legacy `mini-cart` one.
 *
 * Net-new element: <c-cart-rec-toggle> for the "You may also like" checkbox cards.
 *
 * The legacy synchronized-products (Apollo + SmartVibes paired) logic is intentionally
 * NOT reimplemented here — that model is obsolete (single product now).
 * ---------------------------------------------------------------------------
 */
(function () {
  'use strict';

  // Locale-aware root (e.g. "/" or "/en-gb/"). Always ends with a slash.
  var ROOT = (window.Shopify && Shopify.routes && Shopify.routes.root) || '/';
  var CART_SECTION = 'c-mini-cart-v2';

  function endpoint(path) {
    return (ROOT + path).replace(/([^:]\/)\/+/g, '$1');
  }

  function debounceFn(fn, wait) {
    var t;
    return function () {
      var ctx = this;
      var args = arguments;
      clearTimeout(t);
      t = setTimeout(function () {
        fn.apply(ctx, args);
      }, wait);
    };
  }

  function getSectionInner(html, selector) {
    var doc = new DOMParser().parseFromString(html, 'text/html');
    var el = doc.querySelector(selector || '.shopify-section');
    return el ? el.innerHTML : null;
  }

  // The single source of truth for which sections the drawer refreshes.
  // `id` MUST equal the section filename so product-form.js (which sends
  // `getSectionsToRender().map(s => s.id)` as the `sections` param) requests
  // the right sections AND so renderContents() can find the DOM container by id.
  function sectionsToRender() {
    return [
      { id: CART_SECTION, section: CART_SECTION, selector: '.shopify-section' },
      { id: 'cart-icon-bubble', section: 'cart-icon-bubble', selector: '.shopify-section' },
      { id: 'mobile-cart-icon-bubble', section: 'mobile-cart-icon-bubble', selector: '.shopify-section' },
    ];
  }

  function renderSections(sections) {
    if (!sections) return;
    sectionsToRender().forEach(function (s) {
      var el = document.getElementById(s.id);
      if (!el || !sections[s.section]) return;
      var inner = getSectionInner(sections[s.section], s.selector);
      if (inner != null) el.innerHTML = inner;
    });
  }

  function postJSON(path, payload) {
    return fetch(endpoint(path), {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(payload),
    }).then(function (r) {
      return r.json();
    });
  }

  function announceCartUpdate(source) {
    document.dispatchEvent(new CustomEvent('cart:updated', { detail: { source: source } }));
    if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
      publish(PUB_SUB_EVENTS.cartUpdate, { source: source });
    }
  }

  /* ------------------------------------------------------------------ *
   *  <mini-cart>  — the drawer controller (lazy load + open + re-render)
   * ------------------------------------------------------------------ */
  class MiniCartV2 extends HTMLElement {
    connectedCallback() {
      this.drawer = this.closest('cart-drawer') || document.querySelector('cart-drawer');
      if (this._observed) return;
      this._observed = true;
      this._observer = new IntersectionObserver(this.onIntersect.bind(this));
      this._observer.observe(this);
    }

    onIntersect(entries, observer) {
      if (!entries[0].isIntersecting) return;
      observer.unobserve(this);
      this.load();
    }

    load() {
      if (this._loaded || !this.dataset.url) return;
      this._loaded = true;
      fetch(this.dataset.url)
        .then(function (r) {
          return r.text();
        })
        .then((html) => {
          var inner = getSectionInner(html, '.shopify-section');
          if (inner != null) this.innerHTML = inner;
          document.dispatchEvent(new CustomEvent('cartdrawer:opened'));
        })
        .catch(function (e) {
          console.error('[c-mini-cart-v2] initial load failed', e);
        });
    }

    open() {
      if (!this.drawer) this.drawer = document.querySelector('cart-drawer');
      if (!this.drawer) return;
      var details = this.drawer.querySelector('details');
      if (details && details.hasAttribute('open')) return;
      if (typeof this.drawer.openMenuDrawer === 'function') this.drawer.openMenuDrawer();
    }

    // Called by product-form.js / color-swatches.js / global.js after add-to-cart.
    renderContents(state) {
      this.productId = state && state.id;
      renderSections(state && state.sections);
      this.open();
    }

    getSectionsToRender() {
      return sectionsToRender();
    }

    getSectionInnerHTML(html, selector) {
      return getSectionInner(html, selector || '.shopify-section');
    }

    setActiveElement(element) {
      this.activeElement = element;
    }
  }

  /* ------------------------------------------------------------------ *
   *  <cart-items>  — quantity changes + line removal
   * ------------------------------------------------------------------ */
  class CartItemsV2 extends HTMLElement {
    constructor() {
      super();
      this.debouncedOnChange = debounceFn(this.onChange.bind(this), 300);
    }

    connectedCallback() {
      this.addEventListener('change', this.debouncedOnChange);
    }

    onChange(event) {
      var input = event.target;
      if (!input || input.getAttribute('name') !== 'updates[]') return;
      var line = input.dataset.index;
      if (!line) return;
      this.updateQuantity(line, input.value);
    }

    updateQuantity(line, quantity) {
      this.enableLoading(line);
      var sections = sectionsToRender().map(function (s) {
        return s.section;
      });

      postJSON('cart/change.js', {
        line: line,
        quantity: quantity,
        sections: sections,
        sections_url: window.location.pathname,
      })
        .then((state) => {
          if (state && state.status) {
            console.warn('[c-mini-cart-v2] change.js error', state);
          }
          renderSections(state && state.sections);
          announceCartUpdate('cart-items');
        })
        .catch(function (e) {
          console.error('[c-mini-cart-v2] updateQuantity failed', e);
        })
        .finally(() => {
          this.disableLoading();
        });
    }

    renderContents(state) {
      renderSections(state && state.sections);
    }

    getSectionsToRender() {
      return sectionsToRender();
    }

    getSectionInnerHTML(html, selector) {
      return getSectionInner(html, selector || '.shopify-section');
    }

    // Swaps a line's variant (e.g. a color swatch pick) in place. The Cart Ajax
    // API has no "change this line's variant" call, so this is a remove-then-add:
    // 1) zero out the old line, 2) add the target variant back with the same
    // quantity/properties/selling plan. The line's position in the list shifts
    // to the bottom as a result — an accepted tradeoff of that two-step call.
    swapVariant(line, targetVariantId, quantity, properties, sellingPlanId) {
      this.enableLoading(line);
      var sections = sectionsToRender().map(function (s) {
        return s.section;
      });

      postJSON('cart/change.js', { line: line, quantity: 0 })
        .then((state) => {
          if (state && state.status) {
            throw new Error(state.description || state.message || 'remove failed');
          }
          var item = { id: Number(targetVariantId), quantity: quantity || 1 };
          if (properties) item.properties = properties;
          if (sellingPlanId) item.selling_plan = Number(sellingPlanId);
          return postJSON('cart/add.js', {
            items: [item],
            sections: sections,
            sections_url: window.location.pathname,
          });
        })
        .then((state) => {
          if (state && state.status && state.status !== 200) {
            throw new Error(state.description || state.message || 'add failed');
          }
          renderSections(state && state.sections);
          announceCartUpdate('cart-line-color-picker');
        })
        .catch((e) => {
          console.error('[c-mini-cart-v2] swapVariant failed', e);
          // The remove call may have already gone through even though the add
          // failed (e.g. the target ran out of stock mid-swap), so the DOM can
          // no longer be trusted — re-sync it with the cart's real state.
          this.refresh();
        })
        .finally(() => {
          this.disableLoading();
        });
    }

    // Re-renders the tracked sections from the cart's current state without
    // changing anything, to resync the DOM after a failed swapVariant.
    refresh() {
      var sections = sectionsToRender().map(function (s) {
        return s.section;
      });
      return postJSON('cart/update.js', {
        sections: sections,
        sections_url: window.location.pathname,
      })
        .then((state) => {
          renderSections(state && state.sections);
          announceCartUpdate('cart-items-refresh');
        })
        .catch(function (e) {
          console.error('[c-mini-cart-v2] refresh failed', e);
        });
    }

    enableLoading(line) {
      this.classList.add('c-mc__items--busy');
      var lineEl = this.querySelector('[data-line="' + line + '"]');
      var overlay = lineEl && lineEl.querySelector('.c-mc__line-loading');
      if (overlay) overlay.hidden = false;
    }

    disableLoading() {
      this.classList.remove('c-mc__items--busy');
      // Re-hide any line overlay. On success the section is re-rendered (fresh
      // snippet, already hidden); this matters on the error path, where the DOM
      // is NOT replaced and would otherwise keep a stuck loading veil.
      this.querySelectorAll('.c-mc__line-loading').forEach(function (overlay) {
        overlay.hidden = true;
      });
    }
  }

  /* ------------------------------------------------------------------ *
   *  <cart-remove-button>  — removes a line (quantity → 0)
   * ------------------------------------------------------------------ */
  class CartRemoveButtonV2 extends HTMLElement {
    constructor() {
      super();
      this.addEventListener('click', (event) => {
        event.preventDefault();
        var items = this.closest('cart-items');
        if (items) items.updateQuantity(this.dataset.index, 0);
      });
    }
  }

  /* ------------------------------------------------------------------ *
   *  <cart-line-color-picker>  — swatch chip that expands, swaps a line's
   *  color from the drawer via <details>/<summary> (native disclosure, no
   *  toggle JS needed)
   * ------------------------------------------------------------------ */
  class CartLineColorPickerV2 extends HTMLElement {
    connectedCallback() {
      if (this._bound) return;
      this._bound = true;
      this.addEventListener('change', this.onChange.bind(this));

      // Drive the panel entirely off a JS-owned class rather than the
      // native <details>/[open] pair: MenuDrawer (assets/global.js) binds
      // its own click handling to every <summary> it finds, but only once,
      // in its constructor — before this line's markup exists (it arrives
      // later via the drawer's AJAX content swap), so it never sees this
      // element, and how that interacts with the browser's own default
      // <summary> toggle isn't reliable to depend on. `.is-open` is the
      // only thing CSS keys off for panel visibility, set/cleared here, so
      // there is nothing else in the page that can override it.
      var summary = this.querySelector('summary');
      var details = this.querySelector('details');
      if (summary && details) {
        summary.addEventListener('click', function (event) {
          event.preventDefault();
          var isOpen = details.classList.toggle('is-open');
          details.open = isOpen;
        });
      }
    }

    onChange(event) {
      var input = event.target;
      if (!input || input.type !== 'radio') return;

      var details = this.querySelector('details');
      if (details) {
        details.open = false;
        details.classList.remove('is-open');
      }

      var items = this.closest('cart-items');
      if (!items) return;

      var targetVariantId = input.value;
      var currentVariantId = this.dataset.currentVariantId;
      if (!targetVariantId || targetVariantId === currentVariantId) return;

      var propsScript = this.querySelector('[data-line-properties]');
      var properties = null;
      if (propsScript) {
        try {
          properties = JSON.parse(propsScript.textContent);
        } catch (e) {
          properties = null;
        }
      }

      items.swapVariant(
        this.dataset.line,
        targetVariantId,
        Number(this.dataset.quantity) || 1,
        properties,
        this.dataset.sellingPlanId
      );
    }
  }

  // Clicking outside an open color-chip closes it, same as any other
  // dropdown/disclosure in the drawer.
  document.addEventListener('click', function (event) {
    document.querySelectorAll('.c-mc__color-chip.is-open').forEach(function (details) {
      if (!details.contains(event.target)) {
        details.open = false;
        details.classList.remove('is-open');
      }
    });
  });

  /* ------------------------------------------------------------------ *
   *  <c-cart-rec-toggle>  — "You may also like" checkbox add / remove
   * ------------------------------------------------------------------ */
  class CartRecToggleV2 extends HTMLElement {
    constructor() {
      super();
      this.checkbox = this.querySelector('input[type="checkbox"]');
      if (this.checkbox) this.checkbox.addEventListener('change', this.onToggle.bind(this));
    }

    onToggle() {
      if (this.busy) return;
      this.busy = true;
      this.classList.add('is-busy');

      var checked = this.checkbox.checked;
      var variantId = this.dataset.variantId;
      var sections = sectionsToRender().map(function (s) {
        return s.section;
      });

      var request = checked
        ? postJSON('cart/add.js', {
            items: [{ id: Number(variantId), quantity: 1 }],
            sections: sections,
            sections_url: window.location.pathname,
          })
        : postJSON('cart/change.js', {
            id: variantId,
            quantity: 0,
            sections: sections,
            sections_url: window.location.pathname,
          });

      request
        .then((state) => {
          if (state && state.status && state.status !== 200) {
            throw new Error(state.description || state.message || 'cart error');
          }
          renderSections(state && state.sections);
          announceCartUpdate('c-cart-rec-toggle');
        })
        .catch((e) => {
          console.error('[c-mini-cart-v2] recommendation toggle failed', e);
          if (this.checkbox) this.checkbox.checked = !checked; // revert on error
          this.busy = false;
          this.classList.remove('is-busy');
        });
      // On success the whole drawer is re-rendered and this element is replaced,
      // so there is no need to clear `busy` in that path.
    }
  }

  /* ------------------------------------------------------------------ *
   *  Register (guarded so it never double-defines)
   * ------------------------------------------------------------------ */
  if (!customElements.get('mini-cart')) customElements.define('mini-cart', MiniCartV2);
  if (!customElements.get('cart-items')) customElements.define('cart-items', CartItemsV2);
  if (!customElements.get('cart-remove-button')) customElements.define('cart-remove-button', CartRemoveButtonV2);
  if (!customElements.get('cart-line-color-picker')) customElements.define('cart-line-color-picker', CartLineColorPickerV2);
  if (!customElements.get('c-cart-rec-toggle')) customElements.define('c-cart-rec-toggle', CartRecToggleV2);
})();
